import axios from "axios";
import {errorUtils, menuApi as cassanovaMenuApi} from "./cassanova";

// Feature flag to switch between backend proxy and direct Cassanova API
const USE_DIRECT_API = process.env.REACT_APP_USE_DIRECT_API !== "false"; // Default: true

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Legacy backend API client (kept for fallback)
const apiClient = axios.create({
  baseURL: API,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Legacy API implementation using backend proxy
 * @private
 */
const legacyMenuApi = {
  getCategories: async () => {
    const response = await apiClient.get("/menu/categories");
    return response.data;
  },

  getProducts: async (categoryId, start = 0, limit = 50) => {
    const params = {start, limit};
    if (categoryId) {
      params.category_id = categoryId;
    }
    const response = await apiClient.get("/menu/products", {params});
    return response.data;
  },

  getAllProducts: async () => {
    const response = await apiClient.get("/menu/all-products");
    return response.data;
  },
};

/**
 * Enhanced error handler with logging and user-friendly messages
 * @param {Error} error - The error object
 * @param {string} operation - The operation that failed
 * @returns {Error} Enhanced error with user-friendly message
 */
const handleApiError = (error, operation) => {
  console.error(`[API Error] ${operation}:`, error);

  // If it's a Cassanova API error with categorization
  if (error.code && errorUtils.ErrorCodes[error.code]) {
    const userMessage = errorUtils.getErrorMessage(error);
    console.error(`[API Error Details] Code: ${error.code}, Message: ${userMessage}`);

    // Add retry information if applicable
    if (errorUtils.isRetryableError(error)) {
      console.warn(`[API Error] This error is retryable. Consider implementing retry logic.`);
    }

    // Create enhanced error
    const enhancedError = new Error(userMessage);
    enhancedError.originalError = error;
    enhancedError.code = error.code;
    enhancedError.isRetryable = errorUtils.isRetryableError(error);
    enhancedError.isAuthError = errorUtils.isAuthError(error);
    enhancedError.isNetworkError = errorUtils.isNetworkError(error);

    return enhancedError;
  }

  // Handle legacy axios errors
  if (error.response) {
    const status = error.response.status;
    let message = "Si è verificato un errore durante il caricamento dei dati.";

    if (status === 404) {
      message = "Dati non trovati.";
    } else if (status === 500) {
      message = "Errore del server. Riprova più tardi.";
    } else if (status === 401 || status === 403) {
      message = "Errore di autenticazione. Riprova più tardi.";
    }

    const enhancedError = new Error(message);
    enhancedError.originalError = error;
    enhancedError.status = status;
    return enhancedError;
  }

  // Network errors
  if (error.request) {
    const enhancedError = new Error("Errore di connessione. Verifica la tua connessione internet.");
    enhancedError.originalError = error;
    enhancedError.isNetworkError = true;
    return enhancedError;
  }

  // Generic error
  const enhancedError = new Error("Si è verificato un errore imprevisto.");
  enhancedError.originalError = error;
  return enhancedError;
};

/**
 * Menu API - Public interface with automatic backend selection
 *
 * This API automatically switches between the new Cassanova direct API
 * and the legacy backend proxy based on the USE_DIRECT_API flag.
 *
 * @namespace menuApi
 */
export const menuApi = {
  /**
   * Get all menu categories
   * @returns {Promise<Object>} Categories response with categories array and totalCount
   * @throws {Error} Enhanced error with user-friendly message
   */
  getCategories: async () => {
    try {
      console.log(`[API] Fetching categories using ${USE_DIRECT_API ? "Cassanova Direct API" : "Backend Proxy"}`);

      if (USE_DIRECT_API) {
        return await cassanovaMenuApi.getCategories();
      } else {
        return await legacyMenuApi.getCategories();
      }
    } catch (error) {
      throw handleApiError(error, "getCategories");
    }
  },

  /**
   * Get products with optional category filtering
   * @param {string|null} categoryId - Optional category ID to filter products
   * @param {number} start - Starting index for pagination (default: 0)
   * @param {number} limit - Maximum number of products to return (default: 50)
   * @returns {Promise<Object>} Products response with products array and totalCount
   * @throws {Error} Enhanced error with user-friendly message
   */
  getProducts: async (categoryId, start = 0, limit = 50) => {
    try {
      console.log(`[API] Fetching products using ${USE_DIRECT_API ? "Cassanova Direct API" : "Backend Proxy"}`, {
        categoryId,
        start,
        limit
      });

      if (USE_DIRECT_API) {
        return await cassanovaMenuApi.getProducts(categoryId, start, limit);
      } else {
        return await legacyMenuApi.getProducts(categoryId, start, limit);
      }
    } catch (error) {
      throw handleApiError(error, "getProducts");
    }
  },

  /**
   * Get all products from all categories
   * @returns {Promise<Object>} Response with all products array and totalCount
   * @throws {Error} Enhanced error with user-friendly message
   */
  getAllProducts: async () => {
    try {
      console.log(`[API] Fetching all products using ${USE_DIRECT_API ? "Cassanova Direct API" : "Backend Proxy"}`);

      if (USE_DIRECT_API) {
        return await cassanovaMenuApi.getAllProducts();
      } else {
        return await legacyMenuApi.getAllProducts();
      }
    } catch (error) {
      throw handleApiError(error, "getAllProducts");
    }
  },
};

/**
 * Get current API mode information
 * @returns {Object} API mode information
 */
export const getApiInfo = () => ({
  mode: USE_DIRECT_API ? "direct" : "proxy",
  description: USE_DIRECT_API
      ? "Using Cassanova Direct API"
      : "Using Backend Proxy",
  flag: "REACT_APP_USE_DIRECT_API",
  currentValue: USE_DIRECT_API,
});

// Log API mode on module load
console.log(`[API] Initialized in ${USE_DIRECT_API ? "DIRECT" : "PROXY"} mode`);
console.log(`[API] To switch modes, set REACT_APP_USE_DIRECT_API environment variable`);

export default apiClient;
